/**
 * Filters component
 * Handles inline filter bar functionality for search
 */

import { api } from '../services/api.js';

// Module-level state
let loadedSubjects = [];
let selectedSubjects = [];

/**
 * Initialize filters bar
 * @param {Function} onFilterChange - Callback when filters change
 * @param {string} initialSubjectId - Subject ID to pre-select from URL
 */
export async function initFilters(onFilterChange, initialSubjectId = null) {
  const subjectSelect = document.getElementById('subject-select');
  const clearFiltersBtn = document.getElementById('clear-filters');
  const dateFromInput = document.getElementById('date-from');
  const dateToInput = document.getElementById('date-to');
  const applyDateBtn = document.getElementById('apply-date-filter');

  if (!subjectSelect) return;

  // Load subjects into dropdown
  await loadSubjects(subjectSelect);

  // Initialize from URL parameters (after subjects are loaded so names resolve)
  initFiltersFromURL(initialSubjectId);

  // Subject dropdown: selecting an item adds it as a tag
  subjectSelect.addEventListener('change', () => {
    const value = subjectSelect.value;
    if (!value) return;

    const subject = loadedSubjects.find(s => String(s.id) === String(value));
    if (subject && !selectedSubjects.find(s => String(s.id) === String(value))) {
      selectedSubjects.push({ id: subject.id, name: subject.name });
      updateActiveFiltersDisplay();
      onFilterChange(getActiveFilters());
    }
    subjectSelect.value = '';
  });

  // Clear all filters
  if (clearFiltersBtn) {
    clearFiltersBtn.addEventListener('click', () => {
      clearAllFilters();
      onFilterChange(getActiveFilters());
    });
  }

  // Date range apply button
  if (applyDateBtn) {
    applyDateBtn.addEventListener('click', () => {
      updateActiveFiltersDisplay();
      onFilterChange(getActiveFilters());
    });
  }

  // Enter key in date inputs
  [dateFromInput, dateToInput].forEach(input => {
    if (input) {
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          updateActiveFiltersDisplay();
          onFilterChange(getActiveFilters());
        }
      });
    }
  });
}

/**
 * Load subjects from API and populate select dropdown
 */
async function loadSubjects(select) {
  if (!select) return;

  try {
    const { data, error } = await api.getSubjects();

    if (error || !data || data.length === 0) return;

    loadedSubjects = data;

    const options = data.map(subject =>
      `<option value="${subject.id}">${escapeHtml(subject.name)}</option>`
    ).join('');

    select.innerHTML = `<option value="">Subject area...</option>${options}`;
  } catch (error) {
    console.error('Error loading subjects:', error);
  }
}

/**
 * Get currently active filters
 * @returns {Object} Active filters object
 */
export function getActiveFilters() {
  const filters = {
    subjects: [...selectedSubjects],
    dateFrom: null,
    dateTo: null
  };

  const dateFromInput = document.getElementById('date-from');
  const dateToInput = document.getElementById('date-to');

  if (dateFromInput && dateFromInput.value) {
    filters.dateFrom = parseInt(dateFromInput.value, 10);
  }
  if (dateToInput && dateToInput.value) {
    filters.dateTo = parseInt(dateToInput.value, 10);
  }

  return filters;
}

/**
 * Update the active filters display (tags row below filter bar)
 */
function updateActiveFiltersDisplay() {
  const activeFiltersContainer = document.getElementById('active-filters');
  const activeFiltersList = document.getElementById('active-filters-list');

  if (!activeFiltersContainer || !activeFiltersList) return;

  const filters = getActiveFilters();
  const tags = [];

  filters.subjects.forEach(subject => {
    tags.push({ type: 'subject', id: subject.id, label: subject.name });
  });

  if (filters.dateFrom || filters.dateTo) {
    let dateLabel = 'Date: ';
    if (filters.dateFrom && filters.dateTo) {
      dateLabel += `${filters.dateFrom}–${filters.dateTo}`;
    } else if (filters.dateFrom) {
      dateLabel += `From ${filters.dateFrom}`;
    } else {
      dateLabel += `Until ${filters.dateTo}`;
    }
    tags.push({ type: 'date', label: dateLabel });
  }

  if (tags.length === 0) {
    activeFiltersContainer.classList.add('hidden');
    return;
  }

  activeFiltersContainer.classList.remove('hidden');

  const tagsHTML = tags.map(tag => `
    <div class="filter-tag" data-type="${tag.type}" data-id="${tag.id || ''}">
      <span>${escapeHtml(tag.label)}</span>
      <button type="button" aria-label="Remove filter" data-type="${tag.type}" data-id="${tag.id || ''}">×</button>
    </div>
  `).join('');

  activeFiltersList.innerHTML = tagsHTML;

  activeFiltersList.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('click', (e) => {
      removeFilter(e.target.dataset.type, e.target.dataset.id);
    });
  });
}

/**
 * Clear all active filters
 */
function clearAllFilters() {
  selectedSubjects = [];

  const dateFromInput = document.getElementById('date-from');
  const dateToInput = document.getElementById('date-to');
  if (dateFromInput) dateFromInput.value = '';
  if (dateToInput) dateToInput.value = '';

  updateActiveFiltersDisplay();
}

/**
 * Remove a specific filter
 * @param {string} type - Filter type (subject, date)
 * @param {string} id - Filter ID (for subjects)
 */
function removeFilter(type, id) {
  if (type === 'subject') {
    selectedSubjects = selectedSubjects.filter(s => String(s.id) !== String(id));
  } else if (type === 'date') {
    const dateFromInput = document.getElementById('date-from');
    const dateToInput = document.getElementById('date-to');
    if (dateFromInput) dateFromInput.value = '';
    if (dateToInput) dateToInput.value = '';
  }

  updateActiveFiltersDisplay();
  document.dispatchEvent(new CustomEvent('filterchange'));
}

/**
 * Initialize filters from URL parameters
 * @param {string} initialSubjectId - Subject ID from URL parameter
 */
function initFiltersFromURL(initialSubjectId = null) {
  const urlParams = new URLSearchParams(window.location.search);

  // Parse subjects from URL (comma-separated list)
  const subjectsParam = urlParams.get('subjects');
  if (subjectsParam) {
    subjectsParam.split(',').filter(s => s).forEach(id => {
      const subject = loadedSubjects.find(s => String(s.id) === String(id));
      if (subject && !selectedSubjects.find(s => String(s.id) === String(id))) {
        selectedSubjects.push({ id: subject.id, name: subject.name });
      }
    });
  }

  // Handle single subject parameter (from clickable tags on detail pages)
  const singleSubjectId = urlParams.get('subject') || initialSubjectId;
  if (singleSubjectId && !selectedSubjects.find(s => String(s.id) === String(singleSubjectId))) {
    const subject = loadedSubjects.find(s => String(s.id) === String(singleSubjectId));
    if (subject) {
      selectedSubjects.push({ id: subject.id, name: subject.name });
    } else {
      // Fallback if subject not found in loaded list
      selectedSubjects.push({ id: singleSubjectId, name: `Subject ${singleSubjectId}` });
    }
  }

  // Parse date range from URL
  const dateFrom = urlParams.get('dateFrom');
  const dateTo = urlParams.get('dateTo');
  const dateFromInput = document.getElementById('date-from');
  const dateToInput = document.getElementById('date-to');
  if (dateFrom && dateFromInput) dateFromInput.value = dateFrom;
  if (dateTo && dateToInput) dateToInput.value = dateTo;

  updateActiveFiltersDisplay();
}

/**
 * Convert filters to URL parameters
 * @param {Object} filters - Active filters
 * @returns {URLSearchParams} URL parameters
 */
export function filtersToURLParams(filters) {
  const params = new URLSearchParams();

  if (filters.subjects && filters.subjects.length > 0) {
    params.set('subjects', filters.subjects.map(s => s.id).join(','));
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom.toString());
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo.toString());
  }

  return params;
}

/**
 * Build Directus filter object from active filters
 * @param {Object} filters - Active filters
 * @returns {Array} Directus filter conditions
 */
export function buildDirectusFilter(filters) {
  const directusFilters = [];

  if (filters.subjects && filters.subjects.length > 0) {
    directusFilters.push({
      subjects: {
        subjects_id: {
          _in: filters.subjects.map(s => s.id)
        }
      }
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const dateFilter = {};
    if (filters.dateFrom) dateFilter._gte = `${filters.dateFrom}-01-01`;
    if (filters.dateTo) dateFilter._lte = `${filters.dateTo}-12-31`;

    directusFilters.push({
      _or: [
        { publication_date: dateFilter },
        { start_date: dateFilter },
        { created_at: dateFilter }
      ]
    });
  }

  return directusFilters;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
